import { jsPDF } from "jspdf";

/**
 * Loads an image from URL and returns HTMLImageElement
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // For data URLs or same-origin resources, do not set crossOrigin
    // Setting crossOrigin for data: URIs will cause errors in some browsers.
    try {
      if (typeof url === 'string' && !url.startsWith('data:')) {
        img.crossOrigin = 'anonymous';
      }
    } catch (e) {
      // ignore and proceed without crossOrigin
    }

    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });
}

/**
 * Ensures the web font is ready for canvas rendering
 */
async function ensureCanvasFontsLoaded() {
  // Make sure browser has loaded the font family
  // (works in modern browsers)
  if (document?.fonts?.load) {
    await document.fonts.load('16px "Noto Sans Devanagari"');
    await document.fonts.ready;
  }
}

/**
 * Draw the header onto a high-res canvas and return PNG data URL
 * Text is fully editable via HEADER constant.
 */
async function buildHeaderPng({ logoUrl, clinicName = 'Sanjeevani' }) {
  await ensureCanvasFontsLoaded();

  // ====== EDITABLE TEXT (change anytime) ======
  const TEXT = {
    doctorTop: [
      "डा. बी.पी. सिंह",
      "बी. एस. सी., बी.ए.एम.एस",
      "विशेष - पंचकर्म विकिरण Reg. No. 45433",
      "Mob.: 9935379515,9839611010",
      "E-mail: bps664@gmail.com",
    ],
    doctorBottom: ["आजीवन सदस्य - एन.ए.पी.ए.आर.", "- विश्व आयुर्वेद परिषद"],
    clinicHindi: "संजीवनी क्लीनिक एंव पंचकर्म चिकित्सा केन्द्र",
    website: "Website: https://sanjeevaniayurvedic.in",
  };

  // ====== STYLE / POSITIONS (mm) ======
  // If you want “micro-perfect”, you will only tweak numbers here.
  const STYLE = {
    headerMmW: 210,
    // slightly taller header to accommodate larger logo
    headerMmH: 58,

    marginL: 15,
    marginR: 15,
    top: 7,

    // LOGO SIZE BOX (compacted so it stays above the bottom divider line and leaves room for title)
    logo: { x: 12, y: 7.5, w: 42, h: 36 },

    // reserve right side for doctor block so title never overlaps
    doctorBlockW: 66,

    // title / website baseline (moved downward to avoid overlapping right-side doctor content)
    titleY: 41.2,
    websiteY: 46.5,

    // double line
    lineY: 50,
    lineGap: 1.2,

    // colors closer to your pad
    colors: {
      brown: "#A46F3D",
      maroon: "#b33b3b",
      dark: "#17334f",
      green: "#10572d", // darker mehndi shade
    },

    // fonts
    doctorNamePx: 15.2,     // ≈ 4.0mm
    doctorLinePx: 11.4,     // ≈ 3.0mm
    doctorBottomPx: 11.2,   // ≈ 3.0mm
    titleStartPx: 32.0,     // base size to start auto-fit
    titleMinPx: 20.0,       // minimum size when shrinking
    websitePx: 12.0,        // ≈ 3.2mm

    // spacing
    doctorNameGapMm: 4.2,
    doctorLineGapMm: 3.55,
    doctorBottomGapMm: 3.45,

    // line thickness (px)
    lineThickPx: 1.1,
    lineThinPx: 0.7,

  };

  // ====== Canvas setup (FIXED scaling math) ======
  const pxPerMm = 96 / 25.4; // 3.779527...
  const scale = 4;

  const baseW = Math.round(STYLE.headerMmW * pxPerMm);
  const baseH = Math.round(STYLE.headerMmH * pxPerMm);

  const canvas = document.createElement("canvas");
  canvas.width = baseW * scale;
  canvas.height = baseH * scale;

  const ctx = canvas.getContext("2d");
  ctx.setTransform(scale, 0, 0, scale, 0, 0); // <-- correct, consistent
  ctx.imageSmoothingEnabled = true;

  const mmToPx = (mm) => mm * pxPerMm;

  // background (use base coords, NOT scaled size)
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, baseW, baseH);

  const { brown, maroon, dark, green } = STYLE.colors;

  // ===== Helpers =====
  const drawRightMultiline = (lines, xMm, yMm, pxSize, color, gapMm, weight = 600) => {
    ctx.textAlign = "right";
    ctx.fillStyle = color;
    ctx.font = `${weight} ${pxSize}px "Noto Serif Devanagari"`;
    let y = yMm;
    for (const line of lines) {
      ctx.fillText(line, mmToPx(xMm), mmToPx(y));
      y += gapMm;
    }
    return y;
  };

  const fitCenteredText = (text, centerMmX, yMm, startPx, minPx, maxWidthMm, weight = 700) => {
    let size = startPx;
    ctx.textAlign = "center";
    while (size > minPx) {
      ctx.font = `${weight} ${size}px "Noto Serif Devanagari"`;
      if (ctx.measureText(text).width <= mmToPx(maxWidthMm)) break;
      size -= 0.6; // shrink step (smooth)
    }
    ctx.fillText(text, mmToPx(centerMmX), mmToPx(yMm));
    return size;
  };

  // ===== Logo (contain + centered) =====
  let actualLogoRightPx = mmToPx(STYLE.logo.x + STYLE.logo.w);
  try {
    const logo = await loadImage(logoUrl);

    const destX = mmToPx(STYLE.logo.x);
    const destY = mmToPx(STYLE.logo.y);
    const destW = mmToPx(STYLE.logo.w);
    const destH = mmToPx(STYLE.logo.h);

    const imgW = logo.naturalWidth || logo.width;
    const imgH = logo.naturalHeight || logo.height;

    const s = Math.min(destW / imgW, destH / imgH);
    const drawW = imgW * s;
    const drawH = imgH * s;
    const drawX = destX + (destW - drawW) / 2;
    const drawY = destY + (destH - drawH) / 2;

    ctx.drawImage(
      logo,
      drawX,
      drawY,
      drawW,
      drawH
    );
    actualLogoRightPx = drawX + drawW;
  } catch {
    // optional placeholder if logo not found
    ctx.strokeStyle = brown;
    ctx.lineWidth = 1;
    ctx.strokeRect(mmToPx(STYLE.logo.x), mmToPx(STYLE.logo.y), mmToPx(STYLE.logo.w), mmToPx(STYLE.logo.h));
    actualLogoRightPx = mmToPx(STYLE.logo.x + STYLE.logo.w);
  }

  // ===== Doctor block (top-right) =====
  const doctorRightX = STYLE.headerMmW - STYLE.marginR;
  let dy = STYLE.top + 2.2;

  // name
  ctx.textAlign = "right";
  ctx.fillStyle = maroon;
  ctx.font = `700 ${STYLE.doctorNamePx}px "Noto Serif Devanagari"`;
  ctx.fillText(TEXT.doctorTop[0], mmToPx(doctorRightX), mmToPx(dy));
  dy += STYLE.doctorNameGapMm;

  // other lines (dark)
  dy = drawRightMultiline(TEXT.doctorTop.slice(1), doctorRightX, dy, STYLE.doctorLinePx, dark, STYLE.doctorLineGapMm, 600);


  // bottom lines (maroon)
  drawRightMultiline(TEXT.doctorBottom, doctorRightX, dy, STYLE.doctorBottomPx, maroon, STYLE.doctorBottomGapMm, 600);

  // ===== Center title (auto-fit, with clear margin after logo) =====
  const logoTextGapPx = mmToPx(6); // 6mm clear gap after the logo to guarantee no overlap
  const leftStartPx = actualLogoRightPx + logoTextGapPx;
  const rightMarginPx = mmToPx(STYLE.headerMmW - STYLE.marginR);
  const maxWidthPx = rightMarginPx - leftStartPx;

  // Fit title: first Hindi word (cursive & slightly bigger) + remaining Hindi text (less bold)
  const hindiTitle = TEXT.clinicHindi;
  const words = String(hindiTitle).trim().split(/\s+/);
  const firstHindi = words.shift() || hindiTitle;
  const restHindi = words.join(' ');

  // Auto-fit loop: start with a baseline size and adjust to the available width
  let hindiPx = STYLE.titleStartPx;
  const intraWordGapPx = mmToPx(3.5);

  let firstPx = Math.round(hindiPx * 1.15);
  let firstW = 0;
  let restWLocal = 0;
  let totalW = 0;

  while (hindiPx > STYLE.titleMinPx) {
    firstPx = Math.round(hindiPx * 1.15);

    ctx.font = `italic 600 ${firstPx}px "Noto Serif Devanagari"`;
    firstW = ctx.measureText(firstHindi).width;
    ctx.font = `600 ${hindiPx}px "Noto Serif Devanagari"`;
    restWLocal = restHindi ? ctx.measureText(restHindi).width : 0;

    totalW = firstW + (restHindi ? intraWordGapPx + restWLocal : 0);

    if (totalW <= maxWidthPx) break;
    hindiPx -= 0.8;
  }

  // Draw the first word (cursive via italic) slightly larger
  const startXpx = leftStartPx;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = green;
  ctx.font = `italic 600 ${firstPx}px "Noto Serif Devanagari"`;
  ctx.fillText(firstHindi, startXpx, mmToPx(STYLE.titleY));

  // Draw the rest in slightly less bold (600)
  if (restHindi) {
    ctx.font = `600 ${hindiPx}px "Noto Serif Devanagari"`;
    ctx.fillText(restHindi, startXpx + firstW + intraWordGapPx, mmToPx(STYLE.titleY));
  }

  // Place website just below the clinic title (tight spacing, respecting STYLE.websiteY)
  const titleDescentMm = (Math.max(firstPx, hindiPx) * 0.25) / pxPerMm;
  const websiteAscentMm = (STYLE.websitePx * 0.75) / pxPerMm;
  const websiteDescentMm = (STYLE.websitePx * 0.25) / pxPerMm;

  let websiteYmm = STYLE.websiteY;
  const minWebsiteY = STYLE.titleY + titleDescentMm + websiteAscentMm + 0.8;
  if (websiteYmm < minWebsiteY) {
    websiteYmm = minWebsiteY;
  }
  const maxWebsiteY = STYLE.lineY - 1.8 - websiteDescentMm;
  if (websiteYmm > maxWebsiteY) {
    websiteYmm = maxWebsiteY;
  }

  // Center website underneath the clinic title block
  const titleCenterXpx = startXpx + totalW / 2;
  ctx.textAlign = 'center';
  ctx.fillStyle = maroon;
  ctx.font = `700 ${STYLE.websitePx}px Arial`;
  ctx.fillText(TEXT.website, titleCenterXpx, mmToPx(websiteYmm));

  // ===== Double brown lines =====
  ctx.strokeStyle = brown;

  ctx.lineWidth = STYLE.lineThickPx;
  ctx.beginPath();
  ctx.moveTo(mmToPx(STYLE.marginL), mmToPx(STYLE.lineY));
  ctx.lineTo(mmToPx(STYLE.headerMmW - STYLE.marginR), mmToPx(STYLE.lineY));
  ctx.stroke();

  ctx.lineWidth = STYLE.lineThinPx;
  ctx.beginPath();
  ctx.moveTo(mmToPx(STYLE.marginL), mmToPx(STYLE.lineY + STYLE.lineGap));
  ctx.lineTo(mmToPx(STYLE.headerMmW - STYLE.marginR), mmToPx(STYLE.lineY + STYLE.lineGap));
  ctx.stroke();

  return { png: canvas.toDataURL("image/png"), headerMmH: STYLE.headerMmH };
}


/**
 * Draw footer as image (Hindi + email), center aligned
 */
async function buildFooterPng() {
  await ensureCanvasFontsLoaded();

  // Footer text (EDITABLE)
  const FOOTER_TEXT = [
    "1. संजीवनी क्लीनिक विशाल खण्ड-1, गोमती नगर, लखनऊ",
    "2. संजीवनी आयुर्वेदिक सेन्टर 3 ए/12, विशाल खण्ड, गोमती नगर, लखनऊ",
    "सभी प्रकार की आयुर्वेदिक चिकित्सा का एकमात्र केन्द्र-",
    "जहाँ पंचकर्म, क्षारसूत्र, अग्निकर्म, तथा जलौकावचारण की व्यवस्था है।"
  ];

  const EMAIL = "E-mail: sanjeevaniayurvedictreatment@gmail.com";

  // Layout (mm)
  const footerMmW = 210;
  const footerMmH = 28;

  const pxPerMm = 96 / 25.4;
  const scale = 4;

  const baseW = Math.round(footerMmW * pxPerMm);
  const baseH = Math.round(footerMmH * pxPerMm);

  const canvas = document.createElement("canvas");
  canvas.width = baseW * scale;
  canvas.height = baseH * scale;

  const ctx = canvas.getContext("2d");
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  const mmToPx = (mm) => mm * pxPerMm;

  // Colors (MATCH HEADER)
  const brown = "#A46F3D";
  const maroon = "#b33b3b";
  const dark = "#17334f";

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, baseW, baseH);

  // ===== Footer style (local) =====
  const FOOTER_STYLE = {
    marginL: 15,
    marginR: 15,
    // distance from top of footer box where the separator lines are drawn (mm)
    lineY: 1,
    lineGap: 1.2,
    lineThickPx: 1.1,
    lineThinPx: 0.7,
  };

  // ===== Double brown lines (top of footer) =====
  ctx.strokeStyle = brown;

  ctx.lineWidth = FOOTER_STYLE.lineThickPx;
  ctx.beginPath();
  ctx.moveTo(mmToPx(FOOTER_STYLE.marginL), mmToPx(FOOTER_STYLE.lineY));
  ctx.lineTo(mmToPx(footerMmW - FOOTER_STYLE.marginR), mmToPx(FOOTER_STYLE.lineY));
  ctx.stroke();

  ctx.lineWidth = FOOTER_STYLE.lineThinPx;
  ctx.beginPath();
  ctx.moveTo(mmToPx(FOOTER_STYLE.marginL), mmToPx(FOOTER_STYLE.lineY + FOOTER_STYLE.lineGap));
  ctx.lineTo(mmToPx(footerMmW - FOOTER_STYLE.marginR), mmToPx(FOOTER_STYLE.lineY + FOOTER_STYLE.lineGap));
  ctx.stroke();

  // Footer text (center aligned)
  ctx.textAlign = "center";
  ctx.fillStyle = dark;
  ctx.font = `600 11.8px "Noto Serif Devanagari"`;

  let y = 7;
  const lineGap = 4.8;
  FOOTER_TEXT.forEach(line => {
    ctx.fillText(line, mmToPx(footerMmW / 2), mmToPx(y));
    y += lineGap;
  });

  // Email (same color as header website) — move up slightly so it's fully visible
  ctx.fillStyle = maroon;
  ctx.font = `700 12.2px Arial`;
  const emailYOffsetMm = 0; // raise email by 4mm so it stays inside the footer box
  ctx.fillText(EMAIL, mmToPx(footerMmW / 2), mmToPx(y + emailYOffsetMm));

  return { png: canvas.toDataURL("image/png"), footerMmH };
}

/**
 * Fetch patient details by patient_id
 */
async function fetchPatientById(patientId) {
  const res = await fetch("http://127.0.0.1:8000/api/patients");
  if (!res.ok) throw new Error("Failed to fetch patients");

  const patients = await res.json();

  // adjust if API response is wrapped (data, results, etc.)
  return patients.find(p => p.id === patientId) || null;
}
async function textToImage(text) {
  await ensureCanvasFontsLoaded();

  const pxPerMm = 96 / 25.4;
  const scale = 3;

  // ⬆ Increase height to safely fit Devanagari matra
  const W_MM = 30;
  const H_MM = 14; // 🔥 was 10 → THIS FIXES CUTTING

  const w = W_MM * pxPerMm;
  const h = H_MM * pxPerMm;

  const canvas = document.createElement("canvas");
  canvas.width = w * scale;
  canvas.height = h * scale;

  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);

  // Background transparent (important)
  ctx.clearRect(0, 0, w, h);

  ctx.fillStyle = "#17334f";
  ctx.font = '600 14px "Noto Serif Devanagari"';

  // 🔑 THIS IS THE MOST IMPORTANT PART
  ctx.textBaseline = "alphabetic";

  // Draw text LOWER so top matra never clips
  const TEXT_X = 2;
  const TEXT_Y = h * 0.75; // 🔥 magic alignment for Hindi

  ctx.fillText(text, TEXT_X, TEXT_Y);

  return canvas.toDataURL("image/png");
}


/**
 * Generate PDF with header that looks exactly like template.
 * Header stays editable via HEADER object in buildHeaderPng.
 */
export async function generatePrescriptionPDF(data) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const PAGE_W = 210;
  const PAGE_H = doc.internal.pageSize.getHeight();

  // ================= FETCH PATIENT =================
  let patient = null;
  if (data?.patient_id) {
    try {
      const res = await fetch("http://127.0.0.1:8000/api/patients");
      const patients = await res.json();
      patient = patients.find(p => p.id === data.patient_id) || null;
    } catch (e) {
      console.error("Patient fetch failed", e);
    }
  }

  // ================= HEADER =================
  // Allow logo to be passed in, or default to standard public path
  const logoUrl = data?.clinic?.logo || "/logo.png";

  const { png: headerPng, headerMmH } = await buildHeaderPng({
    logoUrl,
    clinicName: data?.clinic?.name || "Sanjeevani",
  });

  doc.addImage(headerPng, "PNG", 0, 0, PAGE_W, headerMmH);

  // ================= FOOTER =================
  const { png: footerPng, footerMmH } = await buildFooterPng();
  doc.addImage(footerPng, "PNG", 0, PAGE_H - footerMmH - 5, PAGE_W, footerMmH);

  // ================= BODY METRICS =================
  const ML = 15;
  const MR = 15;
  const LEFT_COL_W = 30;

  const BODY_TOP = headerMmH + 2;
  const FOOTER_LINE_Y = PAGE_H - footerMmH - 4;

  // ================= REF / DATE =================
  doc.setFontSize(10);
  doc.setTextColor("#17334f");

  doc.text("Ref.", ML, BODY_TOP);
  doc.setLineDash([1.2, 1.2]);
  doc.line(ML + 10, BODY_TOP + 0.5, ML + 70, BODY_TOP + 0.5);

  doc.text("Date", PAGE_W - MR - 35, BODY_TOP);
  doc.line(PAGE_W - MR - 25, BODY_TOP + 0.5, PAGE_W - MR, BODY_TOP + 0.5);
  doc.setLineDash([]);

  const visitDateText = data.visit_date || "";
  if (visitDateText) {
    doc.setFontSize(11);
    doc.text(visitDateText, PAGE_W - MR - 20, BODY_TOP - 0.5);
    doc.setFontSize(10);
  }
  doc.setLineDash([]);

  // ================= VERTICAL DIVIDER =================
  const vTop = BODY_TOP + 6;
  const vBottom = FOOTER_LINE_Y;

  doc.setDrawColor("#A46F3D");
  doc.setLineWidth(0.4);
  doc.line(ML + LEFT_COL_W, vTop, ML + LEFT_COL_W, vBottom);
  doc.setLineWidth(0.2);
  doc.line(ML + LEFT_COL_W + 1.2, vTop, ML + LEFT_COL_W + 1.2, vBottom);

  // ================= DYNAMIC BODY CONTENT =================
  const LEFT_X = ML;
  const RIGHT_X = ML + LEFT_COL_W + 6;
  const RIGHT_W = PAGE_W - MR - RIGHT_X;
  const LH = 6;
  const SECTION_GAP = 8;

  let currentY = vTop + 6;

  // Helper: Draw Section
  const drawSection = async (labelHindi, drawContentFn) => {
    // 1. Draw Hindi Label (as Image)
    const labelPng = await textToImage(labelHindi);
    // Draw content first to measure height, OR just draw label at top
    // label image is approx 10mm tall, text starts at top
    doc.addImage(labelPng, "PNG", LEFT_X, currentY, 30, 10);

    // 2. Draw Content (Right side)
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0); // Black content

    // Capture starting Y for content
    const startY = currentY + 7; // slight adjustment so text aligns with label visual baseline
    const endY = drawContentFn(RIGHT_X, startY);

    // 3. Move Y down
    // Ensure we move down by at least the label height or content height
    const sectionH = Math.max(10, (endY - startY) + 4);
    currentY += sectionH + SECTION_GAP;
  };

  // --- 1. PATIENT DETAILS (रोगी विवरण) ---
  await drawSection("रोगी विवरण", (x, y) => {
    let dy = y;

    // Name
    const pName = patient?.name || data.patient_name || "";
    doc.text(`Name       : ${pName}`, x, dy); dy += LH;

    // Age / Gender
    const pAge = patient?.age || data.patient_age || "";
    const pGender = patient?.gender || data.patient_gender || "";
    if (pAge || pGender) {
      doc.text(`Age/Sex    : ${pAge} / ${pGender}`, x, dy); dy += LH;
    }

    // Phone
    const pPhone = patient?.phone || data.patient_phone || "";
    if (pPhone) {
      doc.text(`Phone      : ${pPhone}`, x, dy); dy += LH;
    }

    // Email
    const pEmail = patient?.email || data.patient_email || "";
    if (pEmail) {
      doc.text(`Email      : ${pEmail}`, x, dy); dy += LH;
    }

    // Address
    const pAddress = patient?.address || data.patient_address || "";
    if (pAddress) {
      const addrPrefix = "Address    : ";
      const availableW = RIGHT_W - doc.getTextWidth(addrPrefix);
      const addrLines = doc.splitTextToSize(pAddress, availableW);

      doc.text(addrPrefix, x, dy);
      // multi-line address handling
      if (addrLines.length > 0) {
        doc.text(addrLines[0], x + doc.getTextWidth(addrPrefix), dy);
        for (let i = 1; i < addrLines.length; i++) {
          dy += LH;
          doc.text(addrLines[i], x + doc.getTextWidth(addrPrefix), dy);
        }
      }
      dy += LH;
    }


    return dy;
  });

  // --- 2. DIET (आहार) ---
  await drawSection("आहार", (x, y) => {
    const text = data.diet || "-";
    const lines = doc.splitTextToSize(text, RIGHT_W);
    doc.text(lines, x, y);
    return y + (lines.length * LH);
  });

  // --- 3. LIFESTYLE (विहार) ---
  await drawSection("विहार", (x, y) => {
    const text = data.lifestyle || "-";
    const lines = doc.splitTextToSize(text, RIGHT_W);
    doc.text(lines, x, y);
    return y + (lines.length * LH);
  });

  // --- 4. MEDICINES (चिकित्सा) ---
  await drawSection("चिकित्सा", (x, y) => {
    const meds = data.medicines || [];
    let dy = y;

    if (meds.length === 0) {
      doc.text("No medicines prescribed", x, dy);
      return dy + LH;
    }

    meds.forEach((m, i) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${i + 1}. ${m.name}`, x, dy);
      dy += LH;

      doc.setFont("helvetica", "normal");
      doc.text(`Dosage    : ${m.dosage || "-"}`, x, dy); dy += LH;
      doc.text(`Frequency : ${m.frequency || "-"}`, x, dy); dy += LH;
      doc.text(`Duration  : ${m.duration || "-"} days`, x, dy); dy += LH + 2;
    });

    return dy;
  });


  // --- 5. NOTES (विशेष टिप्पणी) ---
  // Only render if notes exist
  if (data.notes) {
    await drawSection("विशेष टिप्पणी", (x, y) => {
      const text = data.notes;
      const lines = doc.splitTextToSize(text, RIGHT_W);
      doc.text(lines, x, y);
      return y + (lines.length * LH);
    });
  }

  // ================= FOLLOW-UP DATE (FIXED POSITION LIKE DATE) =================
  if (data.follow_up_date) {
    const FY = FOOTER_LINE_Y - 8; // consistent gap above footer

    doc.setFontSize(10);
    doc.setTextColor("#17334f");

    // Label
    doc.text("Follow-up", PAGE_W - MR - 50, FY);

    // Dotted line
    doc.setLineDash([1.2, 1.2]);
    doc.line(PAGE_W - MR - 30, FY + 0.5, PAGE_W - MR, FY + 0.5);
    doc.setLineDash([]);

    // Date value
    doc.setFontSize(11);
    doc.text(data.follow_up_date, PAGE_W - MR - 24, FY - 0.5);
  }


  doc.save(`prescription-${data.patient_name || "unknown"}.pdf`);
}


